import { TFunction } from 'i18next'
import { Button } from 'vnetwork-ui'
import {
    DataTableColumn,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import { FilterItemConfig } from '@/components/ui/data-table-customized'
import EditIcons from '@/icons/EditIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { Cv } from '../../types'
import {
    CV_SCREEN_RESULTS,
    FINAL_STATUS,
    DEPARTMENT,
    useCvUrlClick,
} from '../../constants'
import { getAllJobPublicOptions } from '@/modules/Jobs/services'
import { getCvSourcesSelect } from '../../services'

/**
 * CV URL cell with file preview support
 */
const CVUrlCell: React.FC<{ row: Cv }> = ({ row }) => {
    const { handleCvUrlClick, isGettingPresignedUrl } = useCvUrlClick()

    if (!row?.url) return null

    const isFile = row?.type_file === 'file'

    return (
        <Button
            onClick={() => handleCvUrlClick(row.url, isFile, row?.name)}
            disabled={isGettingPresignedUrl}
            variant="text"
            style={{
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                minWidth: 'auto',
                textTransform: 'none',
                fontSize: 'inherit',
            }}
        >
            {isGettingPresignedUrl ? 'Loading...' : 'Xem chi tiết'}
        </Button>
    )
}

/**
 * Column definitions for Resume list
 * All sortable fields based on API documentation (12 fields)
 */
export const getResumeColumns = (t: TFunction): DataTableColumn<Cv>[] => [
    {
        key: 'id',
        title: 'ID',
        width: 80,
        sortable: true,
        pinned: 'left',
        formatter: {
            type: 'link',
            pathKey: 'talentPoolDetail',
            pathParams: (value) => ({ id: value }),
            className: 'text-primary hover:underline',
        },
    },
    {
        key: 'name',
        title: t('name'),
        width: 200,
        sortable: true, // ✅ API sortable
        pinned: 'left',
        cellClassName: 'capitalize',
        formatter: {
            type: 'link',
            pathKey: 'talentPoolDetail',
            pathParams: (_value, row) => ({ id: row?.id }),
            className: 'text-primary hover:underline',
        },
    },
    {
        key: 'email',
        title: t('email', { defaultValue: 'Email' }),
        width: 200,
        sortable: true, // ✅ API sortable
    },
    {
        key: 'job_title',
        title: t('position-applied'),
        width: 300,
        sortable: true, // ✅ API sortable
    },
    {
        key: 'department',
        title: t('department-cv'),
        width: 150,
        sortable: true, // ✅ API sortable
    },
    {
        key: 'type_of_position',
        title: t('type-of-position', { defaultValue: 'Loại vị trí' }),
        width: 150,
        sortable: true, // ✅ API sortable
    },
    {
        key: 'address',
        title: t('address-cv'),
        width: 450,
        sortable: false, // ❌ Not sortable per API
    },
    {
        key: 'day_of_birth',
        title: t('day-of-birthday'),
        width: 150,
        sortable: false, // ❌ Not sortable per API
        formatter: {
            type: 'date',
            format: 'DD/MM/YYYY',
        },
    },
    {
        key: 'created_at',
        title: t('application-date'),
        width: 150,
        sortable: true, // ✅ API sortable
        formatter: {
            type: 'date',
            format: 'DD/MM/YYYY',
        },
    },
    {
        key: 'updated_at',
        title: t('updated-at', { defaultValue: 'Cập nhật' }),
        width: 150,
        sortable: true, // ✅ API sortable
        formatter: {
            type: 'date',
            format: 'DD/MM/YYYY',
        },
    },
    {
        key: 'cover_letter_source_name',
        title: t('source'),
        width: 150,
        sortable: true, // ✅ API sortable
    },
    {
        key: 'cover_letter_screen_result',
        title: t('cv-screen-results'),
        width: 200,
        sortable: true, // ✅ API sortable
        render: (value) => {
            const result = CV_SCREEN_RESULTS.find(
                (item) => item.label === value
            )
            return <span className="text-sm">{result?.label ?? ''}</span>
        },
    },
    {
        key: 'final_status',
        title: t('cv-final-status'),
        width: 200,
        sortable: true, // ✅ API sortable
        render: (value) => {
            const result = FINAL_STATUS.find((item) => item.label === value)
            return <span className="text-sm">{result?.label ?? ''}</span>
        },
    },
    {
        key: 'interview_time',
        title: t('interview-time', { defaultValue: 'Thời gian phỏng vấn' }),
        width: 180,
        sortable: true, // ✅ API sortable
        formatter: {
            type: 'date',
            format: 'DD/MM/YYYY HH:mm',
        },
    },
    {
        key: 'url',
        title: t('url-cv'),
        width: 200,
        sortable: false, // ❌ Not sortable per API
        render: (value, row) => <CVUrlCell row={row} />,
    },
]

/**
 * Filter items for Resume list
 */
export const getResumeFilters = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'job_title',
        type: 'async-select',
        label: t('position-applied'),
        placeholder: t('position-applied'),
        asyncOptions: getAllJobPublicOptions,
    },
    {
        name: 'cover_letter_source_name',
        type: 'async-select',
        label: t('source'),
        placeholder: t('source'),
        asyncOptions: getCvSourcesSelect,
    },
    {
        name: 'department',
        type: 'select',
        label: t('department-cv'),
        placeholder: t('department-cv'),
        options: DEPARTMENT,
    },
    {
        name: 'created_at',
        type: 'date-range',
        label: t('application-date'),
        placeholder: t('application-date'),
    },
]

/**
 * Action definitions for Resume list
 */
export const getResumeActions = ({
    t,
    onEdit,
    onDelete,
}: {
    t: TFunction
    onEdit: (row: Cv) => void
    onDelete: (row: Cv) => void
}): DataTableAction<Cv>[] => [
    {
        key: 'edit',
        label: t('edit'),
        icon: <EditIcons className="w-4 h-4" />,
        onClick: onEdit,
        variant: 'default',
    },
    {
        key: 'delete',
        label: t('delete'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        onClick: onDelete,
        variant: 'danger',
        className: 'text-red-600 hover:text-red-700 hover:bg-red-50',
    },
]
